<script setup>
// ============================================================================
// BonBoZ: thêm mẫu trả lời — NHIỀU MẪU trong một lần.
//
// Bản gốc mỗi lần chỉ thêm được một mẫu: nhập vài chục mẫu lúc dựng trang là
// vài chục lần mở hộp, gõ, lưu, đóng. Ở đây mỗi dòng là một mẫu, và chủ đề với
// danh sách trang đặt MỘT lần cho cả loạt — đó mới là chỗ tốn thời gian nhất.
//
// DÁN TỪ BẢNG TÍNH: dán thẳng cột từ Excel/Sheet, mỗi dòng `mã tắt | nội dung`
// (hoặc ngăn bằng Tab như khi copy hai cột). Dán xong vẫn là các dòng sửa được,
// không phải nhập thẳng vào cơ sở dữ liệu.
//
// Không có API tạo hàng loạt: gọi lần lượt từng mẫu. Lỗi ở giữa chừng thì
// những mẫu đã tạo VẪN GIỮ, hộp báo rõ mẫu nào hỏng — chứ không âm thầm bỏ hết
// công gõ của người ta.
// ============================================================================
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import Modal from 'dashboard/components/Modal.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import ChonAnhMau from './ChonAnhMau.vue';
import NhapTinThem from './NhapTinThem.vue';
import { dsChuDe, napChuDe } from 'dashboard/helper/bonboz/chuDeMau';
import { ganMauChoTrang } from 'dashboard/helper/bonboz/ganMauTrang';

const props = defineProps({
  responseContent: { type: String, default: '' },
  onClose: { type: Function, default: () => {} },
});

const { t } = useI18n();
const store = useStore();
const inboxes = useMapGetter('inboxes/getInboxes');

const dongMoi = (noiDung = '') => ({
  maTat: '',
  noiDung,
  anhIds: [],
  tinThem: [],
  moAnh: false,
});

const show = ref(true);
const dong = ref([dongMoi(props.responseContent)]);
const chuDeId = ref('');
const trangChon = ref([]);
const dangLuu = ref(false);
const hienDan = ref(false);
const vanBanDan = ref('');

napChuDe();
if (!inboxes.value.length) store.dispatch('inboxes/get');

const hopLe = computed(() =>
  dong.value.some(d => d.maTat.trim().length >= 2 && d.noiDung.trim())
);
const soHopLe = computed(
  () =>
    dong.value.filter(d => d.maTat.trim().length >= 2 && d.noiDung.trim())
      .length
);

const them = () => dong.value.push(dongMoi());
const bo = i => {
  dong.value.splice(i, 1);
  if (!dong.value.length) them();
};

const doiTrang = id => {
  const i = trangChon.value.indexOf(id);
  if (i === -1) trangChon.value.push(id);
  else trangChon.value.splice(i, 1);
};

// Nhan ca Tab (copy hai cot tu bang tinh) lan dau | (go tay).
const dan = () => {
  const dongMoiThem = vanBanDan.value
    .split('\n')
    .map(d => d.trim())
    .filter(Boolean)
    .map(d => {
      const [maTat, ...con] = d.split(/\t|\|/);
      return {
        ...dongMoi(con.join('|').trim()),
        maTat: maTat.trim().replace(/^\//, ''),
      };
    })
    .filter(d => d.maTat);
  if (!dongMoiThem.length) {
    useAlert(t('BONBOZ.MAU_HANG_LOAT.DAN_TRONG'));
    return;
  }
  // Bo dong trong san co de khong con mot dong rong lo lung o cuoi.
  const giu = dong.value.filter(d => d.maTat.trim() || d.noiDung.trim());
  dong.value = [...giu, ...dongMoiThem];
  vanBanDan.value = '';
  hienDan.value = false;
};

const luu = async () => {
  if (!hopLe.value || dangLuu.value) return;
  dangLuu.value = true;
  const idMoi = [];
  const hong = [];
  // Loc truoc roi chay: dong con dang go do (thieu ma tat hoac noi dung) thi bo
  // qua chu khong bao loi — nguoi ta hay de mot dong trong o cuoi.
  const canLuu = dong.value.filter(
    d => d.maTat.trim().length >= 2 && d.noiDung.trim()
  );
  try {
    for (let i = 0; i < canLuu.length; i += 1) {
      const d = canLuu[i];
      const maTat = d.maTat.trim();
      try {
        // eslint-disable-next-line no-await-in-loop
        const mau = await store.dispatch('createCannedResponse', {
          short_code: maTat,
          content: d.noiDung,
          anh_ids: d.anhIds,
          tin_them: d.tinThem.map(x => x.trim()).filter(Boolean),
          chu_de_id: chuDeId.value || null,
        });
        if (mau?.id) idMoi.push(mau.id);
      } catch (e) {
        hong.push(maTat);
      }
    }

    if (idMoi.length && trangChon.value.length) {
      const { loi } = await ganMauChoTrang(trangChon.value, idMoi);
      if (loi) useAlert(loi);
    }

    if (hong.length) {
      useAlert(t('BONBOZ.MAU_HANG_LOAT.MOT_SO_HONG', { ds: hong.join(', ') }));
    } else {
      useAlert(t('BONBOZ.MAU_HANG_LOAT.DA_TAO', { so: idMoi.length }));
    }
    if (idMoi.length) props.onClose();
  } finally {
    dangLuu.value = false;
  }
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose" size="medium">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        :header-title="$t('CANNED_MGMT.ADD.TITLE')"
        :header-content="$t('BONBOZ.MAU_HANG_LOAT.MO_TA')"
      />
      <form class="flex flex-col w-full gap-3" @submit.prevent="luu">
        <!-- Chu de + trang: dat MOT lan cho ca loat. -->
        <div class="flex flex-wrap gap-4">
          <label class="!mb-0">
            {{ $t('BONBOZ.CHU_DE.NHAN') }}
            <select v-model="chuDeId" class="!mb-0 !h-9 min-w-[12rem] text-sm">
              <option value="">{{ $t('BONBOZ.CHU_DE.KHONG') }}</option>
              <option v-for="cd in dsChuDe" :key="cd.id" :value="cd.id">
                {{ cd.ten }}
              </option>
            </select>
          </label>
          <div class="flex flex-col gap-1">
            <span class="text-sm text-n-slate-12">
              {{ $t('BONBOZ.MAU_HANG_LOAT.GAN_TRANG') }}
            </span>
            <div class="flex flex-wrap gap-1.5">
              <button
                v-for="ib in inboxes"
                :key="ib.id"
                type="button"
                class="px-2.5 py-1 text-xs rounded-full border"
                :class="
                  trangChon.includes(ib.id)
                    ? 'bg-n-brand/10 border-n-brand text-n-brand font-medium'
                    : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-1'
                "
                @click="doiTrang(ib.id)"
              >
                {{ ib.name }}
              </button>
            </div>
            <span class="text-xs text-n-slate-11">
              {{ $t('BONBOZ.MAU_HANG_LOAT.GAN_THEM') }}
            </span>
          </div>
        </div>

        <!-- Mot dong = mot mau. -->
        <div
          v-for="(d, i) in dong"
          :key="i"
          class="flex flex-col gap-2 p-3 border rounded-lg border-n-weak"
        >
          <div class="flex items-center gap-2">
            <span class="text-xs shrink-0 text-n-slate-11 w-7">
              {{ `#${i + 1}` }}
            </span>
            <input
              v-model="d.maTat"
              type="text"
              class="!mb-0 !h-8 max-w-[12rem] text-sm"
              :placeholder="$t('CANNED_MGMT.ADD.FORM.SHORT_CODE.PLACEHOLDER')"
            />
            <NextButton
              xs
              slate
              :variant="d.anhIds.length ? 'solid' : 'faded'"
              icon="i-lucide-images"
              :label="
                d.anhIds.length
                  ? String(d.anhIds.length)
                  : $t('BONBOZ.MAU_ANH.CHON')
              "
              @click="d.moAnh = !d.moAnh"
            />
            <span class="flex-1" />
            <NextButton
              v-if="dong.length > 1"
              xs
              ruby
              ghost
              icon="i-lucide-x"
              :aria-label="$t('BONBOZ.MAU_HANG_LOAT.BO_DONG')"
              @click="bo(i)"
            />
          </div>
          <WootMessageEditor
            v-model="d.noiDung"
            class="message-editor [&>div]:px-1"
            channel-type="Context::Default"
            enable-variables
            :enable-canned-responses="false"
            :placeholder="$t('CANNED_MGMT.ADD.FORM.CONTENT.PLACEHOLDER')"
          />
          <NhapTinThem v-model="d.tinThem" />
          <ChonAnhMau v-if="d.moAnh" v-model="d.anhIds" />
        </div>

        <div class="flex flex-wrap items-center gap-2">
          <NextButton
            sm
            slate
            faded
            icon="i-lucide-plus"
            :label="$t('BONBOZ.MAU_HANG_LOAT.THEM_DONG')"
            @click="them"
          />
          <NextButton
            sm
            slate
            faded
            icon="i-lucide-clipboard-paste"
            :label="$t('BONBOZ.MAU_HANG_LOAT.DAN')"
            @click="hienDan = !hienDan"
          />
        </div>

        <div v-if="hienDan" class="flex flex-col gap-2">
          <textarea
            v-model="vanBanDan"
            class="!mb-0 text-sm font-mono min-h-[7rem]"
            :placeholder="$t('BONBOZ.MAU_HANG_LOAT.DAN_VD')"
          />
          <div class="flex gap-2">
            <NextButton
              sm
              :label="$t('BONBOZ.MAU_HANG_LOAT.DAN_XONG')"
              :disabled="!vanBanDan.trim()"
              @click="dan"
            />
            <NextButton
              sm
              slate
              faded
              :label="$t('CANNED_MGMT.ADD.CANCEL_BUTTON_TEXT')"
              @click="hienDan = false"
            />
          </div>
        </div>

        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t('CANNED_MGMT.ADD.CANCEL_BUTTON_TEXT')"
            @click.prevent="onClose"
          />
          <NextButton
            type="submit"
            :label="$t('BONBOZ.MAU_HANG_LOAT.LUU', { so: soHopLe })"
            :disabled="!hopLe || dangLuu"
            :is-loading="dangLuu"
          />
        </div>
      </form>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
:deep(.ProseMirror-menubar) {
  @apply hidden;
}

:deep(.ProseMirror-woot-style) {
  @apply min-h-[4rem];
}
</style>
